// TOUR-2 — known highlight targets + permission gates for gated controls.

import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { BUCKETS_REGISTRY } from "@/components/shell/modes/bucketsRegistry";
import {
  FIREFLY_BUCKET_SWITCHER_TARGET_ID,
  fireflyBucketNavTargetId,
} from "./fireflyBucketNavTargets";

export type FireflyTargetDefinition = {
  id: string;
  label: string;
  /** When set, highlight is blocked unless `canAccessPrimaryMode` passes. */
  requiredMode?: PrimaryMode;
};

export const FIREFLY_TARGET_REGISTRY: readonly FireflyTargetDefinition[] = [
  { id: "activity-dock-tab", label: "Firefly tab" },
  { id: "mode-nav-leads", label: "Leads mode tab", requiredMode: "leads" },
  { id: FIREFLY_BUCKET_SWITCHER_TARGET_ID, label: "Workspace bucket switcher" },
  ...BUCKETS_REGISTRY.map((b) => ({
    id: fireflyBucketNavTargetId(b.id),
    label: `${b.label} workspace option`,
  })),
  { id: "capture-lead", label: "New Customer", requiredMode: "leads" },
  { id: "capture-voice", label: "Listen (voice capture)", requiredMode: "leads" },
  { id: "lead-rerun-extract", label: "Re-run extraction", requiredMode: "leads" },
  { id: "send-lead", label: "Send to CRM", requiredMode: "leads" },
  { id: "lead-crm-link", label: "Open in CRM (post-send)", requiredMode: "leads" },
  { id: "crm-send-toast", label: "Send success toast with CRM link", requiredMode: "leads" },
  { id: "unsent-leads-section", label: "Unsent leads (dock section)" },
  { id: "sent-leads-section", label: "Sent leads · past 30 days (dock)" },
  { id: "send-all-unsent", label: "Send all unsent", requiredMode: "leads" },
  { id: "recent-leads-section", label: "Recent leads list", requiredMode: "leads" },
  { id: "ask-firefly", label: "Ask Firefly" },
  { id: "sidebar-firefly-dock", label: "Sidebar Firefly dock entry" },
  { id: "firefly-catalog-chip", label: "Show me what Firefly can do" },
  { id: "firefly-catalog", label: "Firefly tour catalog panel" },
  { id: "ads-log-post", label: "Save post & run analysis", requiredMode: "adsAssistant" },
  { id: "mode-nav-adsAssistant", label: "Ads Assistant mode tab", requiredMode: "adsAssistant" },
  { id: "ads-ingest-tab", label: "Ads Assistant Ingestion tab", requiredMode: "adsAssistant" },
  { id: "ads-suggest-tab", label: "Ads Assistant Suggestion tab", requiredMode: "adsAssistant" },
  { id: "ads-activity-coaching", label: "Ads activity coaching card (dock)" },
  { id: "mode-nav-adsGuidance", label: "Ads Guidance mode tab", requiredMode: "adsGuidance" },
  { id: "mode-nav-audienceData", label: "Audience Data mode tab", requiredMode: "audienceData" },
  { id: "audience-data-surface", label: "Audience Data panel", requiredMode: "audienceData" },
  { id: "audience-data-quality", label: "Audience Data quality", requiredMode: "audienceData" },
  { id: "audience-data-inbound", label: "Audience Data live inbound", requiredMode: "audienceData" },
  { id: "audience-data-settings", label: "Audience Data settings", requiredMode: "audienceData" },
  { id: "ads-guidance-surface", label: "Ads Guidance panel", requiredMode: "adsGuidance" },
  {
    id: "ads-guidance-library",
    label: "Ads Guidance library (categories / templates)",
    requiredMode: "adsGuidance",
  },
  {
    id: "ads-guidance-publish-or-policies",
    label: "Ads Guidance policies / publish path",
    requiredMode: "adsGuidance",
  },
  { id: "mode-nav-opportunities", label: "Opportunities mode tab", requiredMode: "opportunities" },
  { id: "mode-nav-salesConsole", label: "Sales Console mode tab", requiredMode: "salesConsole" },
  { id: "opp-new-opportunity", label: "New opportunity", requiredMode: "opportunities" },
  { id: "opp-list-surface", label: "Opportunity list", requiredMode: "opportunities" },
  { id: "sc-daily-checklist", label: "Daily sales checklist (dock)" },
  { id: "sc-follow-ups", label: "Sales Console follow-up queue", requiredMode: "salesConsole" },
  { id: "sc-deal-todos", label: "Sales Console deal todos", requiredMode: "salesConsole" },
  { id: "sc-rto-calc", label: "RTO term calculator", requiredMode: "salesConsole" },
  { id: "sc-sticky-notes", label: "Sales Console sticky notes", requiredMode: "salesConsole" },
  { id: "mode-nav-training", label: "Training mode tab", requiredMode: "training" },
  { id: "training-roleplay-tab", label: "Training Roleplay tab", requiredMode: "training" },
  { id: "training-ask-tab", label: "Training Ask & policies tab", requiredMode: "training" },
  { id: "training-scenario-list", label: "Roleplay scenario picker", requiredMode: "training" },
  { id: "training-start-roleplay", label: "Start roleplay", requiredMode: "training" },
  { id: "mode-nav-salesIntake", label: "Order Intake mode tab", requiredMode: "salesIntake" },
  { id: "intake-surface", label: "Order Intake panel", requiredMode: "salesIntake" },
  { id: "intake-form", label: "Submit customer order form", requiredMode: "salesIntake" },
  { id: "intake-submit-order", label: "Submit order", requiredMode: "salesIntake" },
  { id: "intake-submitted-list", label: "Your submissions list", requiredMode: "salesIntake" },
  { id: "intake-review-queue", label: "Ops review intake queue", requiredMode: "salesIntake" },
  { id: "intake-variant-picker", label: "Order Intake View as picker", requiredMode: "salesIntake" },
  { id: "mode-nav-formsAndFormats", label: "Documents Editor mode tab", requiredMode: "formsAndFormats" },
  { id: "ff-surface", label: "Documents Editor panel", requiredMode: "formsAndFormats" },
  { id: "ff-documents-tab", label: "Documents sub-tab", requiredMode: "formsAndFormats" },
  { id: "ff-proposals-tab", label: "Proposals sub-tab", requiredMode: "formsAndFormats" },
  { id: "ff-asset-library-tab", label: "Asset library sub-tab", requiredMode: "formsAndFormats" },
  { id: "ff-proposal-preset-list", label: "Proposal product type presets", requiredMode: "formsAndFormats" },
  { id: "ff-proposal-text-editor", label: "Proposal text editor", requiredMode: "formsAndFormats" },
  { id: "ff-proposal-save", label: "Save proposal text override", requiredMode: "formsAndFormats" },
  {
    id: "mode-nav-fulfillmentInventory",
    label: "Fulfillment Inventory mode tab",
    requiredMode: "fulfillmentInventory",
  },
  {
    id: "mode-nav-fulfillmentEntry",
    label: "Fulfillment New inventory mode tab",
    requiredMode: "fulfillmentEntry",
  },
  { id: "ff-inventory-list", label: "Lot inventory list", requiredMode: "fulfillmentInventory" },
  { id: "ff-entry-form", label: "Single-piece entry form", requiredMode: "fulfillmentEntry" },
  { id: "ff-entry-create", label: "Create unit", requiredMode: "fulfillmentEntry" },
  {
    id: "mode-nav-fulfillmentIntake",
    label: "Fulfillment Intake forms mode tab",
    requiredMode: "fulfillmentIntake",
  },
  { id: "ff-intake-queue", label: "Intake form queue", requiredMode: "fulfillmentIntake" },
  {
    id: "ff-intake-needs-filling",
    label: "Intake needs filling section",
    requiredMode: "fulfillmentIntake",
  },
  { id: "ff-intake-fill", label: "Fill intake form", requiredMode: "fulfillmentIntake" },
  {
    id: "ff-intake-needs-review",
    label: "Intake needs review section",
    requiredMode: "fulfillmentIntake",
  },
  { id: "ff-intake-review", label: "Review intake form", requiredMode: "fulfillmentIntake" },
  { id: "mode-nav-knowledgebase", label: "Knowledge Base mode tab", requiredMode: "knowledgebase" },
  { id: "kb-workspace", label: "KB workspace hub", requiredMode: "knowledgebase" },
  { id: "kb-library-tile", label: "KB Library tile", requiredMode: "knowledgebase" },
  { id: "kb-gap-inbox-tile", label: "KB Gap Inbox tile", requiredMode: "knowledgebase" },
  { id: "kb-search", label: "KB library search", requiredMode: "knowledgebase" },
  { id: "kb-search-submit", label: "Search library", requiredMode: "knowledgebase" },
  { id: "kb-results-list", label: "KB search results", requiredMode: "knowledgebase" },
  { id: "kb-editor-tile", label: "KB Editor workspace tile", requiredMode: "knowledgebase" },
  { id: "kb-new-article", label: "New KB article", requiredMode: "knowledgebase" },
  { id: "kb-editor-panel", label: "KB article editor", requiredMode: "knowledgebase" },
  { id: "kb-editor-save", label: "Save KB article", requiredMode: "knowledgebase" },
  { id: "mode-nav-locationTools", label: "Location Tools mode tab", requiredMode: "locationTools" },
  { id: "lt-surface", label: "Location Tools panel", requiredMode: "locationTools" },
  { id: "lt-links-tile", label: "Location links section", requiredMode: "locationTools" },
  { id: "lt-mode-picker", label: "Location tools edit mode", requiredMode: "locationTools" },
  { id: "lt-link-types-grid", label: "Link types grid", requiredMode: "locationTools" },
  { id: "lt-save-links", label: "Save location links", requiredMode: "locationTools" },
  { id: "mode-nav-aiPrompting", label: "AI Prompting mode tab", requiredMode: "aiPrompting" },
  { id: "ap-surface", label: "AI Prompting panel", requiredMode: "aiPrompting" },
  { id: "ap-prompt-stack", label: "Editable prompt cards", requiredMode: "aiPrompting" },
  { id: "ap-suggest-edit", label: "Suggest edit", requiredMode: "aiPrompting" },
  { id: "ap-review-queue", label: "Suggestion review queue", requiredMode: "aiPrompting" },
  { id: "mode-nav-trainingAuthor", label: "Training Authoring mode tab", requiredMode: "trainingAuthor" },
  { id: "ta-surface", label: "Training Authoring panel", requiredMode: "trainingAuthor" },
  { id: "ta-catalog-grid", label: "Roleplay scenario catalog", requiredMode: "trainingAuthor" },
  { id: "ta-new-scenario", label: "New roleplay scenario", requiredMode: "trainingAuthor" },
  { id: "ta-scenario-editor", label: "Roleplay scenario editor", requiredMode: "trainingAuthor" },
  { id: "ta-publish-scenario", label: "Publish roleplay scenario", requiredMode: "trainingAuthor" },
  { id: "mode-nav-admin", label: "Admin mode tab", requiredMode: "admin" },
  { id: "admin-surface", label: "Admin panel surface", requiredMode: "admin" },
  { id: "admin-users-card", label: "App User Admin card", requiredMode: "admin" },
  { id: "admin-pending-approvals", label: "Pending member approvals", requiredMode: "admin" },
  { id: "admin-invite-section", label: "Invite User accordion", requiredMode: "admin" },
  { id: "admin-invite-email", label: "Invite email field", requiredMode: "admin" },
  { id: "admin-invite-role", label: "Invite org role picker", requiredMode: "admin" },
  { id: "admin-invite-send", label: "Send invite button", requiredMode: "admin" },
  { id: "admin-org-settings", label: "Org Administration / Org Settings", requiredMode: "admin" },
  { id: "admin-locations", label: "Locations accordion", requiredMode: "admin" },
  { id: "admin-org-profile-save", label: "Save org settings", requiredMode: "admin" },
  { id: "mode-nav-mfgInventory", label: "Manufacturing Inventory mode tab", requiredMode: "mfgInventory" },
  { id: "mfg-inventory-surface", label: "Manufacturing inventory panel", requiredMode: "mfgInventory" },
  { id: "mfg-materials-list", label: "Materials list with WAC", requiredMode: "mfgInventory" },
  { id: "mfg-receive-lines", label: "Receive lines form", requiredMode: "mfgInventory" },
  { id: "mfg-receive-post", label: "Post receipt button", requiredMode: "mfgInventory" },
  { id: "stale-wac-reminder", label: "Stale WAC dock reminder" },
  { id: "mode-nav-mfgPlants", label: "Manufacturing Plants mode tab", requiredMode: "mfgPlants" },
  { id: "mfg-plants-list", label: "Plants roster list", requiredMode: "mfgPlants" },
  { id: "mfg-plant-detail", label: "Selected plant column / dealers", requiredMode: "mfgPlants" },
  { id: "mode-nav-mfgBuildPipeline", label: "Manufacturing Build pipeline mode tab", requiredMode: "mfgBuildPipeline" },
  { id: "mode-nav-mfgPurchasing", label: "Production Purchasing Assistant mode tab", requiredMode: "mfgPurchasing" },
  { id: "mode-nav-mfgLaborPay", label: "Production Labor pay mode tab", requiredMode: "mfgLaborPay" },
  { id: "mode-nav-mfgBuilderAssistant", label: "Production Builder Assistant mode tab", requiredMode: "mfgBuilderAssistant" },
  { id: "mfg-build-pipeline-units-tab", label: "Build pipeline Units tab", requiredMode: "mfgBuildPipeline" },
  { id: "mfg-build-pipeline-kanban-tab", label: "Build pipeline Plant Kanban tab", requiredMode: "mfgBuildPipeline" },
  { id: "mfg-units-list", label: "Units list / build timeline", requiredMode: "mfgBuildPipeline" },
  { id: "mode-nav-operations", label: "Operations mode tab", requiredMode: "operations" },
  { id: "ops-paperwork-surface", label: "Deals panel", requiredMode: "operations" },
  { id: "ops-missing-paperwork", label: "Overdue / missing paperwork pulse", requiredMode: "operations" },
  { id: "ops-deals-tab", label: "Operations Deals tab", requiredMode: "operations" },
  { id: "ops-locations-tab", label: "Operations Locations tab", requiredMode: "operations" },
  { id: "ops-deals-list", label: "Ops deals list", requiredMode: "operations" },
  { id: "ops-deal-checklist", label: "Deal paperwork checklist", requiredMode: "operations" },
  { id: "mode-nav-rtoTerms", label: "RTO Terms mode tab", requiredMode: "rtoTerms" },
  { id: "rto-terms-surface", label: "RTO Terms panel", requiredMode: "rtoTerms" },
  { id: "rto-mfg-inherit", label: "Use manufacturer RTO terms", requiredMode: "rtoTerms" },
  { id: "rto-terms-table", label: "RTO term definitions table", requiredMode: "rtoTerms" },
  { id: "rto-terms-save", label: "Save RTO terms", requiredMode: "rtoTerms" },
  {
    id: "mode-nav-logisticsRoutes",
    label: "Logistics Routes mode tab",
    requiredMode: "logisticsRoutes",
  },
  { id: "logi-routes-surface", label: "Logistics Routes panel", requiredMode: "logisticsRoutes" },
  { id: "logi-routes-list", label: "Delivery queue list", requiredMode: "logisticsRoutes" },
  { id: "logi-route-detail", label: "Selected route detail", requiredMode: "logisticsRoutes" },
  { id: "logi-route-stops", label: "Route stops timeline", requiredMode: "logisticsRoutes" },
  { id: "logi-dispatch-board", label: "Dispatch board", requiredMode: "logisticsRoutes" },
  {
    id: "mode-nav-logisticsCarriers",
    label: "Logistics Carriers mode tab",
    requiredMode: "logisticsCarriers",
  },
  { id: "logi-carriers-surface", label: "Logistics Carriers panel", requiredMode: "logisticsCarriers" },
  { id: "logi-carriers-new", label: "New carrier button", requiredMode: "logisticsCarriers" },
  { id: "logi-carriers-form", label: "Carrier create form", requiredMode: "logisticsCarriers" },
  { id: "logi-carriers-list", label: "Carriers list", requiredMode: "logisticsCarriers" },
  {
    id: "logi-carrier-active-toggle",
    label: "Carrier deactivate / reactivate",
    requiredMode: "logisticsCarriers",
  },
  {
    id: "mode-nav-platform",
    label: "Platform mode tab",
    requiredMode: "platform",
  },
  { id: "platform-surface", label: "Platform View panel", requiredMode: "platform" },
  { id: "platform-tabs", label: "Platform overview / orgs / users tabs", requiredMode: "platform" },
  { id: "platform-context", label: "Platform context dock card" },
  { id: "dev-simulator", label: "Dev simulator sidebar control" },
  { id: "dev-impersonation-apply", label: "Apply impersonation" },
  { id: "dev-highlight-demo-a", label: "Dev demo button A" },
  { id: "dev-highlight-demo-b", label: "Dev demo button B" },
];

const BY_ID = new Map(FIREFLY_TARGET_REGISTRY.map((t) => [t.id, t]));

export function getFireflyTargetDefinition(targetId: string): FireflyTargetDefinition | null {
  return BY_ID.get(targetId.trim()) ?? null;
}
